import styled from "styled-components";

import { ConditionVariant } from "@components/Condition";
import {
  CardInfoWrapper,
  ConditionActionButton,
  ConditionText,
  DescriptionWrapper,
  InfoButton,
  OptionElementWrapper,
  TextWrapper,
} from "@components/elements/Condition";
import { color, font } from "@runwayhealth/runway-components-react";
import { Condition, ConditionTypes, Medication } from "@types-runway/condition";
import { getProductImage } from "@utils/medication/productImageManager";

const SeeOptionsLink = styled.button`
  color: ${color.brand.primary.medium};
  font-family: ${font.family};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  border: none;
  background: transparent;
  padding: 0;

  &:hover {
    color: ${color.brand.primary.dark};
  }
`;

type PriceDisplayProps = {
  price: number;
  discount?: number;
};

const PriceDisplay = ({ price, discount }: PriceDisplayProps) => (
  <>
    <ConditionText $hasDiscount={Boolean(discount)}>{`$${price}`}</ConditionText>
    {discount && <ConditionText>{`$${price - discount}`}</ConditionText>}
  </>
);

type ConditionBodyProps = {
  condition: Condition;
  removeCondition?: (condition: string) => void;
  addCondition?: (conditionName: string, medication?: Medication) => void;
  discount?: number;
  conditionPrice: number;
  setOverlayPanelIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMedicationSelectorIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  hasMultipleMedications?: boolean;
  selectedMedication?: Medication;
  selectedMedicationName?: string;
  cartItemPresentationStyle?: "card" | "selector";
  variant?: ConditionVariant;
};

const ConditionBody = ({
  condition,
  discount,
  conditionPrice,
  setOverlayPanelIsOpen,
  setMedicationSelectorIsOpen,
  hasMultipleMedications,
  selectedMedication,
  selectedMedicationName,
  removeCondition,
  addCondition,
  cartItemPresentationStyle = "card",
  variant,
}: ConditionBodyProps) => {
  const isOptionCard =
    variant === "upsell" || variant === "comfort-care" || variant === "region-based";
  const useOptionCard = cartItemPresentationStyle === "selector" && isOptionCard;
  const useActionCard = !useOptionCard && variant !== "feed";

  const showActionButtons = variant === "dashboard" || variant === "consultation";

  const infoButtonInline = variant === "feed" && !condition.suggestedMedication?.name;

  return (
    <>
      <img
        width="40px"
        src={getProductImage(condition.product.name as ConditionTypes)}
        alt={condition.suggestedMedication?.name}
      />
      <CardInfoWrapper $inlineInfo={useOptionCard}>
        <DescriptionWrapper $infoButtonInline={infoButtonInline}>
          <ConditionText className="ph-no-capture">{condition.product.name}</ConditionText>
          <TextWrapper $infoButtonInline={infoButtonInline}>
            <ConditionText className="ph-no-capture" $reducedWeight={true}>
              {selectedMedicationName ??
                (hasMultipleMedications ? undefined : condition.suggestedMedication?.name)}
            </ConditionText>
            {variant === "feed" && (
              <div className="info-button-wrapper">
                <InfoButton
                  isIconOnly
                  iconName="Info"
                  variant="outline"
                  onClick={() => {
                    setOverlayPanelIsOpen(true);
                  }}
                ></InfoButton>
              </div>
            )}
          </TextWrapper>
          {variant !== "feed" && hasMultipleMedications && setMedicationSelectorIsOpen && (
            <SeeOptionsLink
              data-testid={`change-med-${condition.product.id}`}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setMedicationSelectorIsOpen(true);
              }}
            >
              See options
            </SeeOptionsLink>
          )}
        </DescriptionWrapper>
        {/** Price description */}
        {useActionCard && (
          <DescriptionWrapper>
            <TextWrapper>
              <PriceDisplay price={conditionPrice} discount={discount} />
              <div className="info-button-wrapper">
                <InfoButton
                  isIconOnly
                  iconName="Info"
                  variant="outline"
                  onClick={() => {
                    setOverlayPanelIsOpen(true);
                  }}
                ></InfoButton>
              </div>
            </TextWrapper>
          </DescriptionWrapper>
        )}
      </CardInfoWrapper>
      {useOptionCard && (
        <OptionElementWrapper>
          <PriceDisplay price={conditionPrice} discount={discount} />
          <TextWrapper $infoButtonInline>
            <InfoButton
              isIconOnly
              iconName="Info"
              variant="outline"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setOverlayPanelIsOpen(true);
              }}
            ></InfoButton>
          </TextWrapper>
        </OptionElementWrapper>
      )}
      {/** End of price description */}
      {showActionButtons && condition.product.name !== ConditionTypes.RUNWAY_PLUS && (
        <ConditionActionButton
          data-testid={`product-${condition.product.id}`}
          isIconOnly
          iconName={removeCondition ? "Trash" : "Plus"}
          variant="outline"
          onClick={() => {
            if (removeCondition) {
              removeCondition(condition.product.name);
            } else if (hasMultipleMedications && !selectedMedication && setMedicationSelectorIsOpen) {
              setMedicationSelectorIsOpen(true);
            } else if (addCondition) {
              addCondition(condition.product.name, selectedMedication);
            }
          }}
        ></ConditionActionButton>
      )}
    </>
  );
};

export default ConditionBody;
